// UpcomingClassCard.jsx
import React, { useState, useEffect } from 'react';
import { MapPin, PlayCircle, Zap, Calendar, ArrowRight, Users } from 'lucide-react';
import moment from 'moment';

const UpcomingClassCard = ({ classItem, onClick }) => {
  const [timeStatus, setTimeStatus] = useState(classItem.timeStatus || 'upcoming');
  const [displayTime, setDisplayTime] = useState(classItem.timeUntil || '');

  useEffect(() => {
    const update = () => {
      const isCompleted = Boolean(
        classItem.hasAttendanceMarked ||
        classItem.attendanceMarked ||
        classItem.timeStatus === 'completed' ||
        classItem.status === 'completed' ||
        classItem.status === 'done' ||
        classItem.attendanceStatus === 'marked' ||
        classItem.isCompleted ||
        classItem.completed === true
      );

      if (isCompleted) {
        setTimeStatus('completed');
        setDisplayTime('Completed');
        return;
      }

      if (classItem.timeStatus && ['completed', 'in_progress', 'starting_soon', 'upcoming', 'ended'].includes(classItem.timeStatus)) {
        setTimeStatus(classItem.timeStatus);
        setDisplayTime(classItem.timeUntil || '');
        return;
      }

      const now = moment();
      const start = moment(classItem.classTime || classItem.startTime);
      const diff = start.diff(now, 'minutes');
      if (diff <= 0 && diff >= -120) setTimeStatus('in_progress');
      else if (diff <= 30 && diff > 0) setTimeStatus('starting_soon');
      else setTimeStatus('upcoming');

      setDisplayTime(start.fromNow());
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [classItem]);

  const variants = {
    in_progress: {
      border: 'border-l-4 border-l-red-500',
      bg: 'bg-white',
      badge: 'bg-red-100 text-red-700',
      icon: <PlayCircle className="w-4 h-4 text-red-600 animate-pulse" />,
      label: 'Now Happening'
    },
    starting_soon: {
      border: 'border-l-4 border-l-amber-400',
      bg: 'bg-white',
      badge: 'bg-amber-100 text-amber-700',
      icon: <Zap className="w-4 h-4 text-amber-600" />,
      label: 'Starting Soon'
    },
    upcoming: {
      border: 'border-l-4 border-l-emerald-500',
      bg: 'bg-white hover:bg-slate-50',
      badge: 'bg-slate-100 text-slate-600',
      icon: <Calendar className="w-4 h-4 text-slate-500" />,
      label: 'Upcoming'
    },
    completed: {
      border: 'border-l-4 border-l-emerald-600',
      bg: 'bg-white/95',
      badge: 'bg-emerald-100 text-emerald-700',
      icon: <Calendar className="w-4 h-4 text-emerald-600" />,
      label: 'Completed'
    }
  };

  let effectiveTimeStatus = timeStatus;
  const currentVariant = variants[effectiveTimeStatus] || variants.upcoming;

  return (
    <div 
      onClick={onClick}
      className={`group relative rounded-xl p-4 shadow-sm border border-slate-200 transition-all duration-200 hover:shadow-md hover:scale-[1.01] cursor-pointer ${currentVariant.border} ${currentVariant.bg}`}
    >
      <div className="flex items-center gap-4">
        {/* Time */}
        <div className="flex flex-col items-center justify-center min-w-[70px]">
          <span className="text-xl font-bold text-slate-800">
            {classItem.timeSlot?.start
              ? moment(classItem.timeSlot.start, 'HH:mm').format('hh:mm A')
              : ''}
          </span>
          <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">{displayTime}</span>
        </div>

        {/* Divider */}
        <div className="w-px h-10 bg-slate-200"></div>

        {/* Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1 ${currentVariant.badge}`}>
              {currentVariant.icon} {currentVariant.label}
            </span>
            {classItem.type === 'Lab' && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-purple-100 text-purple-700 flex items-center gap-1">
                <Users className="w-3 h-3" /> Lab
              </span>
            )}
          </div>
          <h4 className="font-bold text-slate-900">
            {classItem.subjectName || classItem.subjectSnapshot?.subjectName || classItem.subjectId?.name || 'Unscheduled Session'}
          </h4>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{classItem.className || classItem.classId?.className || 'Unknown Class'}</span>
            <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {classItem.room || classItem.roomName || classItem.classRoom || classItem.classroom || 'TBA'}
            </span>
          </div>
        </div>

        {/* Action */}
        <div className="opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0">
          <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
            <ArrowRight className="w-4 h-4 text-indigo-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpcomingClassCard;